import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { PaginatedComments } from '../comments-paginated.model';
import {
  ConversationAccessState,
  ConversationCommentListQuery,
  ConversationDefinition,
  ConversationNormalisedError,
  Conversation,
} from '../models';
import { getConversationDefinitionKey, getConversationIdKey } from '../utils';
import { ConversationApiService } from './conversation-api.service';

export interface ConversationResolutionState {
  loading: boolean;
  exists: boolean;
  missing: boolean;
  forbidden: boolean;
  conversation?: Conversation;
  error?: ConversationNormalisedError;
  requestKey?: string;
}

export interface ConversationCommentListState {
  loading: boolean;
  data?: PaginatedComments;
  error?: ConversationNormalisedError;
  lastRequestKey?: string;
}

export interface FirstCommentSubmissionResult {
  conversationId: number;
  comments: PaginatedComments;
}

@Injectable({ providedIn: 'root' })
export class ConversationStateService {
  readonly conversationState$ =
    new BehaviorSubject<ConversationResolutionState>({
      loading: false,
      exists: false,
      missing: false,
      forbidden: false,
    });

  readonly commentsState$ = new BehaviorSubject<ConversationCommentListState>({
    loading: false,
  });

  readonly accessState$ = new BehaviorSubject<ConversationAccessState>({
    canRead: true,
    canWrite: true,
    canCreate: true,
  });

  private inFlightRequests = new Map<string, Observable<unknown>>();

  constructor(private conversationApiService: ConversationApiService) {}

  resolveConversation(
    definition: ConversationDefinition,
    guestUuid?: string
  ): Observable<ConversationResolutionState> {
    const requestKey = getConversationDefinitionKey(definition);

    this.commentsState$.next({ loading: false });
    this.conversationState$.next({
      loading: true,
      exists: false,
      missing: false,
      forbidden: false,
      requestKey,
    });

    const existingRequest = this.inFlightRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest as Observable<ConversationResolutionState>;
    }

    const request$ = this.conversationApiService
      .previewConversation(definition, guestUuid)
      .pipe(
        switchMap((response) => {
          const conversationId = this.getConversationId(response);
          if (conversationId) {
            return this.conversationApiService
              .getConversation(conversationId, guestUuid)
              .pipe(
                map((conversation) => ({
                  loading: false,
                  exists: true,
                  missing: false,
                  forbidden: false,
                  conversation,
                  requestKey,
                } as ConversationResolutionState))
              );
          }

          return of({
            loading: false,
            exists: false,
            missing: true,
            forbidden: false,
            requestKey,
          } as ConversationResolutionState);
        }),
        tap((state) => {
          this.conversationState$.next(state);

          this.accessState$.next({
            canRead: true,
            canWrite: !state.missing,
            canCreate: state.missing,
            blockedReason: state.missing ? 'missing' : undefined,
            registrationRequired: false,
          });
        }),
        tap({
          error: (error: ConversationNormalisedError) => {
            const forbidden = error?.status === 403;
            this.conversationState$.next({
              loading: false,
              exists: false,
              missing: false,
              forbidden,
              error,
              requestKey,
            });

            this.accessState$.next({
              canRead: !forbidden,
              canWrite: false,
              canCreate: false,
              blockedReason: forbidden ? 'forbidden' : 'error',
              registrationRequired: error?.status === 401,
            });
          },
        }),
        finalize(() => {
          this.inFlightRequests.delete(requestKey);
        }),
        shareReplay(1)
      );

    this.inFlightRequests.set(requestKey, request$);
    return request$;
  }

  loadConversationComments(
    conversationId: number,
    query: ConversationCommentListQuery = {},
    guestUuid?: string
  ): Observable<PaginatedComments> {
    const requestKey = [
      getConversationIdKey(conversationId),
      String(query.page ?? 1),
      String(query.per_page ?? 10),
      query.sort_by ?? 'created_at',
      query.sort_direction ?? 'desc',
    ].join(':');

    this.commentsState$.next({
      ...this.commentsState$.value,
      loading: true,
      lastRequestKey: requestKey,
      error: undefined,
    });

    const existingRequest = this.inFlightRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest as Observable<PaginatedComments>;
    }

    const request$ = this.conversationApiService
      .listCommentsByConversationId(conversationId, query, guestUuid)
      .pipe(
        tap((response) => {
          this.commentsState$.next({
            loading: false,
            data: response,
            lastRequestKey: requestKey,
            error: undefined,
          });
        }),
        tap({
          error: (error: ConversationNormalisedError) => {
            this.commentsState$.next({
              loading: false,
              data: this.commentsState$.value.data,
              lastRequestKey: requestKey,
              error,
            });
          },
        }),
        finalize(() => {
          this.inFlightRequests.delete(requestKey);
        }),
        shareReplay(1)
      );

    this.inFlightRequests.set(requestKey, request$);
    return request$ as Observable<PaginatedComments>;
  }

  submitFirstCommentByDefinition(
    definition: ConversationDefinition,
    message: string,
    query: ConversationCommentListQuery = {},
    guestUuid?: string
  ): Observable<FirstCommentSubmissionResult> {
    const definitionKey = getConversationDefinitionKey(definition);
    const requestKey = `comments.first-comment.${definitionKey}`;

    this.commentsState$.next({
      ...this.commentsState$.value,
      loading: true,
      error: undefined,
      lastRequestKey: requestKey,
    });

    const existingRequest = this.inFlightRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest as Observable<FirstCommentSubmissionResult>;
    }

    const request$ = this.conversationApiService
      .createCommentByDefinition(definition, message, guestUuid)
      .pipe(
        switchMap((createdComment) =>
          this.conversationApiService
            .getConversation(createdComment.conversation_id, guestUuid)
            .pipe(map((conversation) => ({ createdComment, conversation })))
        ),
        tap(({ conversation }) => {
          this.conversationState$.next({
            loading: false,
            exists: true,
            missing: false,
            forbidden: false,
            conversation,
            requestKey: definitionKey,
          });

          this.accessState$.next({
            canRead: true,
            canWrite: true,
            canCreate: true,
            blockedReason: undefined,
            registrationRequired: false,
          });
        }),
        switchMap(({ createdComment }) =>
          this.loadConversationComments(createdComment.conversation_id, query, guestUuid).pipe(
            map((comments) => ({
              conversationId: createdComment.conversation_id,
              comments,
            }))
          )
        ),
        tap({
          error: (error: ConversationNormalisedError) => {
            const forbidden = error?.status === 403;
            this.commentsState$.next({
              ...this.commentsState$.value,
              loading: false,
              error,
              lastRequestKey: requestKey,
            });
            this.accessState$.next({
              canRead: !forbidden,
              canWrite: false,
              canCreate: false,
              blockedReason: forbidden ? 'forbidden' : 'error',
              registrationRequired: error?.status === 401,
            });
          },
        }),
        finalize(() => {
          this.inFlightRequests.delete(requestKey);
        }),
        shareReplay(1)
      );

    this.inFlightRequests.set(requestKey, request$);
    return request$ as Observable<FirstCommentSubmissionResult>;
  }

  private getConversationId(conversationLike: unknown): number | null {
    const id = (conversationLike as { id?: unknown } | null | undefined)?.id;
    return typeof id === 'number' ? id : null;
  }
}
